import { KatButton, KatIcon, KatInput } from "@amzn/katal-react";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useCountrySelection } from "src/components/Contexts/CountrySelectionContext";
import { useNest } from "src/components/Contexts/NestContext";
import { ModalWrapper } from "src/components/Modal/ModalWrapper";
import { useNestPartnersFetcher } from "src/components/Pages/Nest/hooks/useNestPartnersFetcher";
import "./index.css";
import { UserSecretIcon } from "src/components/Icons/UserSecretIcon";

export function shouldSearch(
  countryCode: string,
  partnerCode: string
): boolean {
  if (!countryCode) {
    return false;
  }
  return partnerCode.length === 0 || partnerCode.length >= 2;
}

export default function ImpersonateButton() {
  const { t } = useTranslation();
  const { partnerCode, setPartnerCode } = useNest();
  const [search, setSearch] = useState("");
  const { selectedCountry } = useCountrySelection();
  const { data: partnerCodes } = useNestPartnersFetcher(
    {
      countryCode: selectedCountry?.countryCode ?? "",
      partnerCode: search,
    },
    shouldSearch
  );
  const handleCancel = () => setVisible(false);
  const handleClear = () => {
    setPartnerCode(null);
    setVisible(false);
  };
  const [visible, setVisible] = useState(false);

  const handleSelect = (code: string) => {
    setPartnerCode(code);
    setVisible(false);
  };

  const onSearchInput = (e: KatInput.InputEvent) => {
    const target = e.target as HTMLInputElement;
    setSearch(target.value);
  };

  const title = (
    <div className="nest-impersonate-modal-header">
      <UserSecretIcon />
      <span>{t("nest_impersonate_title")}</span>
    </div>
  );

  return (
    <>
      <button
        className={`nest-impersonate-button ${partnerCode ? "nest-impersonate-button--warning" : ""}`}
        onClick={() => setVisible(true)}
      >
        <div className="nest-impersonate-button-internal">
          <UserSecretIcon />
          {partnerCode
            ? t("nest_impersonate_button_partner_label", { partnerCode })
            : t("nest_impersonate_button")}
        </div>
      </button>
      <ModalWrapper
        title={title}
        visible={visible}
        setVisible={setVisible}
        classNames={["nest-impersonate-modal"]}
        footer={
          <div className="nest-impersonate-footer-buttons">
            <KatButton variant="secondary" onClick={handleCancel}>
              {t("nest_impersonate_cancel")}
            </KatButton>
            <KatButton variant="primary" onClick={handleClear}>
              {t("nest_impersonate_clear")}
            </KatButton>
          </div>
        }
      >
        <div>
          <div className="nest-impersonate-search-label">
            <b>{t("nest_impersonate_search_label")}</b>
          </div>
          {search.length === 1 ? (
            <KatInput
              className="nest-impersonate-input"
              type="search"
              placeholder={t("nest_impersonate_search_placeholder")}
              state="error"
              value={search}
              onInput={onSearchInput}
            />
          ) : (
            <KatInput
              className="nest-impersonate-input"
              type="search"
              placeholder={t("nest_impersonate_search_placeholder")}
              value={search}
              onInput={onSearchInput}
            />
          )}

          <div className="nest-impersonate-footer-text">
            {t("nest_impersonate_footer_text")}
          </div>
          <div className="nest-impersonate-list">
            {partnerCodes.length > 0 ? (
              partnerCodes.map((code) => (
                <div
                  key={code}
                  className="nest-impersonate-list-item"
                  onClick={() => handleSelect(code)}
                >
                  <p>{code}</p>
                  <KatIcon name="chevron-right" size="small" />
                </div>
              ))
            ) : (
              <div className="nest-impersonate-list-item nest-impersonate-no-results">
                {t("nest_impersonate_no_results")}
              </div>
            )}
          </div>
        </div>
      </ModalWrapper>
    </>
  );
}
